import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check, Plus, Search, ExternalLink, Loader2, Store as StoreIcon } from "lucide-react";
import { useActiveStore } from "@/context/ActiveStoreContext";

export function StoreAvatar({ s, size = 24 }) {
  const style = { width: size, height: size, fontSize: Math.max(10, size * 0.42) };
  if (s?.logo) return <img src={s.logo} alt="" style={style} className="rounded-md object-cover shrink-0 bg-slate-100" />;
  return (
    <span style={{ ...style, background: s?.color ? `${s.color}1f` : undefined, color: s?.color || undefined }}
      className={`rounded-md font-bold flex items-center justify-center shrink-0 ${s?.color ? "" : "bg-orange-100 text-orange-600"}`}>
      {s?.name?.[0]?.toUpperCase() || <StoreIcon size={size * 0.6} />}
    </span>
  );
}

const SEARCH_AFTER = 5;   // show the search box once there are more stores than this

/** Top-bar store switcher: active store, searchable list of the vendor's stores, "+ Add New Store" last. */
export default function StoreSwitcher({ onAdd }) {
  const { stores, active, activeId, loading, switching, switchStore } = useActiveStore();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); return undefined; }
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? stores.filter((s) => `${s.name} ${s.slug} ${s.domain || ""}`.toLowerCase().includes(t)) : stores;
  }, [stores, q]);

  if (loading && !active) return <div className="h-9 w-32 rounded-lg bg-slate-100 animate-pulse" aria-hidden />;
  const shown = active || stores[0];

  const pick = async (s) => { setOpen(false); if (s.id !== activeId) await switchStore(s.id); };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} data-testid="store-switcher"
        className="flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-neutral-800 hover:border-blue-300 hover:text-blue-600 transition-colors">
        {switching ? <Loader2 size={16} className="animate-spin text-slate-400" /> : <StoreAvatar s={shown} size={22} />}
        <span className="max-w-[90px] sm:max-w-[170px] truncate" data-testid="store-switcher-name">{shown?.name || "Select store"}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid="store-switcher-menu"
          className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[62px] sm:top-full mt-0 sm:mt-1.5 sm:w-[320px] rounded-xl bg-white border border-slate-200 shadow-xl z-30 overflow-hidden">
          <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-semibold text-slate-500 flex items-center justify-between">
            <span>Your stores ({stores.length})</span>
            {shown && <a href={`/store/${shown.slug}`} target="_blank" rel="noreferrer" data-testid="vendor-view-storefront" className="inline-flex items-center gap-1 text-blue-600 hover:underline">View storefront <ExternalLink size={11} /></a>}
          </div>
          {stores.length > SEARCH_AFTER && (
            <div className="px-3 pb-2 relative">
              <Search size={14} className="absolute left-5 top-[11px] -translate-y-px text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stores…" aria-label="Search stores" data-testid="store-switcher-search"
                className="w-full h-8 rounded-lg border border-slate-200 pl-8 pr-2 text-[13px] outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/15" />
            </div>
          )}
          <ul role="listbox" aria-label="Stores" className="max-h-[min(320px,50vh)] overflow-y-auto px-1.5 pb-1.5 scrollbar-thin">
            {list.map((s) => (
              <li key={s.id} role="option" aria-selected={s.id === activeId}>
                <button type="button" onClick={() => pick(s)} data-testid={`store-option-${s.slug}`}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${s.id === activeId ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <StoreAvatar s={s} size={30} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-[13px] font-semibold truncate ${s.id === activeId ? "text-blue-700" : "text-neutral-900"}`}>{s.name}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{s.domain || `/store/${s.slug}`}</span>
                  </span>
                  {s.id === activeId && <Check size={15} className="text-blue-600 shrink-0" />}
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="px-3 py-5 text-center text-[13px] text-slate-400">No store matches “{q}”.</li>}
          </ul>
          <div className="border-t border-slate-100 p-1.5">
            <button type="button" onClick={() => { setOpen(false); onAdd(); }} data-testid="store-add-new"
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold text-blue-600 hover:bg-blue-50 transition-colors">
              <span className="w-[30px] h-[30px] rounded-md border border-dashed border-blue-300 flex items-center justify-center"><Plus size={15} /></span>
              Add New Store
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
